import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';
import { Lock, Mail, User, ShieldCheck, CalendarDays, Ticket } from 'lucide-react';

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        if (!invitationCode.trim()) {
          toast.error("Code d'inscription requis");
          setLoading(false);
          return;
        }
        const res = await authAPI.register({ email, password, name, invitation_code: invitationCode.trim() });
        login(res.data.token, res.data.user);
        toast.success('Compte créé avec succès');
        navigate('/');
      } else {
        const res = await authAPI.login({ email, password });
        if (res.data.requires_2fa) {
          setNeeds2FA(true);
          setTempToken(res.data.token);
          toast.info('Entrez votre code 2FA');
        } else {
          login(res.data.token, res.data.user);
          toast.success('Connexion réussie');
          navigate('/');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de connexion');
    }
    setLoading(false);
  };

  const handle2FA = async () => {
    if (otpCode.length !== 6) return;
    setLoading(true);
    try {
      const res = await authAPI.verify2FA({ token: tempToken, totp_code: otpCode });
      login(res.data.token, res.data.user);
      toast.success('Connexion réussie');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code 2FA invalide');
      setOtpCode('');
    }
    setLoading(false);
  };

  if (needs2FA) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div data-testid="2fa-form" className="w-full max-w-md bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-200/60 p-8 animate-scale-in">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Vérification 2FA</h1>
              <p className="text-sm text-slate-500">Entrez le code de votre application</p>
            </div>
          </div>
          <div className="flex justify-center mb-6">
            <InputOTP data-testid="2fa-otp-input" maxLength={6} value={otpCode} onChange={setOtpCode}>
              <InputOTPGroup>
                <InputOTPSlot index={0} /><InputOTPSlot index={1} /><InputOTPSlot index={2} />
                <InputOTPSlot index={3} /><InputOTPSlot index={4} /><InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>
          <Button data-testid="2fa-verify-btn" className="w-full h-11 font-semibold" onClick={handle2FA}
            disabled={loading || otpCode.length !== 6}>
            {loading ? 'Vérification...' : 'Vérifier'}
          </Button>
          <button data-testid="2fa-back-btn"
            className="mt-4 w-full text-center text-sm text-slate-500 hover:text-slate-700 transition-colors"
            onClick={() => { setNeeds2FA(false); setOtpCode(''); }}>
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div data-testid="login-form" className="w-full max-w-md bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-200/60 p-8 animate-scale-in">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
            <CalendarDays className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Planning Équipe</h1>
            <p className="text-sm text-slate-500">{isRegister ? 'Créer un compte' : 'Connexion'}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {isRegister && (
            <>
              <div className="space-y-2">
                <Label htmlFor="invitation-code" className="text-slate-700">Code d'inscription</Label>
                <div className="relative">
                  <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.5} />
                  <Input
                    data-testid="register-invitation-code"
                    id="invitation-code"
                    value={invitationCode}
                    onChange={e => setInvitationCode(e.target.value.toUpperCase())}
                    placeholder="XXXX-XXXX"
                    className="pl-10 h-11 font-data tracking-widest"
                    required
                  />
                </div>
                <p className="text-[11px] text-slate-400">Code fourni par votre administrateur</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700">Nom</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.5} />
                  <Input data-testid="register-name-input" id="name" value={name}
                    onChange={e => setName(e.target.value)} placeholder="Votre nom" className="pl-10 h-11" required />
                </div>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-700">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.5} />
              <Input data-testid="login-email-input" id="email" type="email" value={email}
                onChange={e => setEmail(e.target.value)} placeholder="email@example.com" className="pl-10 h-11" required />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.5} />
              <Input data-testid="login-password-input" id="password" type="password" value={password}
                onChange={e => setPassword(e.target.value)} placeholder="Mot de passe" className="pl-10 h-11" required />
            </div>
          </div>

          <Button data-testid="login-submit-btn" type="submit" className="w-full h-11 font-semibold" disabled={loading}>
            {loading ? 'Chargement...' : isRegister ? 'Créer le compte' : 'Se connecter'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button data-testid="toggle-auth-mode-btn"
            className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
            onClick={() => setIsRegister(!isRegister)}>
            {isRegister ? 'Déjà un compte ? Se connecter' : 'Pas de compte ? Créer un compte'}
          </button>
        </div>
      </div>
    </div>
  );
}
